/**
 * Sesotho: Inbox
 *
 * What this file covers: Conversations with clients on every channel: replying, notes, assigning, taking over
 * from the chatbot, canned replies.
 *
 * Tone: Quick and practical, for people answering clients all day. Short labels. When something cannot be sent,
 * say why and what to do, in one sentence.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const inbox: Catalog = {
  'inbox.title': 'Lebokoso le kenang',
  'inbox.empty': 'Ha ho lipuisano hajoale: hokela nomoro ebe u lumella moamoheli ho u romella molaetsa.',
  'inbox.select': 'Khetha puisano ho bala nalane le ho araba.',
  'inbox.noMessages': 'Ha ho melaetsa hajoale',
  'inbox.loadingMessages': 'E kenya melaetsa…',
  'inbox.assign': 'Abela moemeli',
  'inbox.unassigned': 'Ha ea abeloa',
  'inbox.assigned': 'Kabo e nchafalitsoe.',
  'inbox.lock': 'Notlela',
  'inbox.locked': 'Puisano e notletsoe ho uena.',
  'inbox.lockedBy': 'E notletsoe ke',
  'inbox.anotherAgent': 'moemeli e mong',
  'inbox.send': 'Romela',
  'inbox.newline': 'bakeng sa mola o mocha',
  'inbox.canned.noMatch': 'Ha ho likarabo tse lokiselitsoeng tse tsamaellanang.',
  'inbox.canned.manage': 'Laola likarabo tse lokiselitsoeng',
  'inbox.canned.navigate': 'tsamaisa',
  'inbox.canned.insert': 'kenya',
  'inbox.canned.close': 'koala',
  'inbox.tags.title': 'Litšoantšo',
  'inbox.tags.add': 'Eketsa letšoao',
  'inbox.tags.placeholder': 'Lebitso la letšoao…',
  'inbox.tags.suggestions': 'Matšoao a teng',
  'inbox.transfer': 'Fetisetsa',
  'inbox.transfer.title': 'Fetisetsa puisano',
  'inbox.transfer.agent': 'Fetisetsa ho',
  'inbox.transfer.note': 'Tlhokomeliso bakeng sa moemeli ea amohelang (ka boikhethelo)',
  'inbox.transfer.noteprefix': 'E fetiselitsoe ho',
  'inbox.transfer.keeps': 'Litlhokomeliso le matšoao li lula le puisano.',
  'inbox.transfer.done': 'Puisano e fetiselitsoe.',
  'inbox.sla.firstReply': 'Karabo ea pele',
  'inbox.sla.resolve': 'Rarolla',
  'inbox.sla.left': 'e setseng',
  'inbox.sla.over': 'e fetile',
  'inbox.sla.breached': 'SLA e tlōletsoe',
  'inbox.sla.met': 'SLA e phethiloe',
  'inbox.sla.due': 'E lokela ka',
  'inbox.notes.title': 'Litlhokomeliso tsa ka hare',
  'inbox.notes.empty': 'Ha ho litlhokomeliso hajoale.',
  'inbox.notes.agent': 'Moemeli',
  'inbox.notes.add': 'Eketsa tlhokomeliso',
  'inbox.lock.heldBy': 'E notletsoe ke',
  'inbox.lock.until': 'ho fihlela',
  'inbox.lock.mine': 'U tšoere puisano ena',
  'inbox.lock.release': 'Lokolla',
  'inbox.lock.released': 'Senotlolo se lokollotsoe.',
  'inbox.lock.readOnly': 'Ho bala feela ha moemeli e mong a araba.',
  'inbox.resolve': 'Rarolla',
  'inbox.reopen': 'Bula hape',
  'inbox.stateChanged': 'Puisano e nchafalitsoe.',
  'inbox.state.pending': 'E emetse',
  'inbox.state.open': 'E buletsoe',
  'inbox.state.resolved': 'E rarollotsoe',
  'inbox.state.archived': 'E bolokiloe',
  'canned.title': 'Likarabo tse lokiselitsoeng',
  'canned.new': 'Karabo e ncha',
  'canned.edit': 'Fetola karabo',
  'canned.shortcut': 'Tsela e khutšoanyane',
  'canned.titleField': 'Sehlooho',
  'canned.body': 'Molaetsa',
  'canned.language': 'Puo',
  'canned.empty': 'Ha ho likarabo tse lokiselitsoeng hajoale.',
  'canned.saved': 'Karabo e lokiselitsoeng e bolokiloe.',
  'canned.deleted': 'Karabo e lokiselitsoeng e hlakotsoe.',
  'canned.updated': 'E nchafalitsoe',
  'inbox.assignFailed': 'Ho abela ho hlolehile',
  'inbox.lockFailed': 'Ho notlela ho hlolehile',
  'inbox.notes.addFailed': 'Ha ea khona ho eketsa tlhokomeliso.',
  'inbox.releaseFailed': 'Ho lokolla ho hlolehile',
  'inbox.sendFailed': 'Ho romela ho hlolehile',
  'inbox.tags.addFailed': 'Ha ea khona ho eketsa letšoao.',
  'inbox.tags.removeFailed': 'Ha ea khona ho tlosa letšoao.',
  'inbox.transfer.transferFailed': 'Ho fetisetsa ho hlolehile',
  'inbox.updateFailed': 'Ho nchafatsa ho hlolehile',
  'inbox.conversations': 'Lipuisano',
  'inbox.filters.more': 'Lisefo tse ling',
  'inbox.queue.open': 'E buletsoe',
  'inbox.queue.resolved': 'E rarollotsoe',
  'inbox.showingOf': 'Ho bontšoa {{shown}} ho {{total}}',
  'inbox.sla': 'SLA',
  'inbox.tags': 'Litšoaea',
  'inbox.assignedTo.label': 'E abetsoe ho',
  'inbox.composer.emoji': 'Kenya emoji',
  'inbox.composer.canned': 'Kenya karabo e lokiselitsoeng',
  'inbox.composer.hint': 'Tobetsa Enter ho romela, Shift+Enter bakeng sa mola o mocha',
  'inbox.message.sent': 'E rometsoe',
  'inbox.notes.private': 'Lekunutu',
  'inbox.quickReplies.title': 'Likarabo tse potlakileng',
  'inbox.quickReplies.search': 'Batla likarabo',
  'inbox.quickReplies.empty': 'Ha ho likarabo tse lokiselitsoeng hajoale.',
  'inbox.tip': 'Keletso: ngola / lebokoseng la karabo bakeng sa likarabo tse lokiselitsoeng, ebe u tobetsa Enter ho romela.',
  'inbox.sla.onTrack': 'E tsamaea hantle',
  'inbox.sla.dueSoon': 'E se e le haufi',
  'inbox.replyWindow': 'Fensetere ea karabo',
  'inbox.newMessage': 'Molaetsa o mocha',
  'inbox.message.failed': 'Ha ea fihla',
  'inbox.message.failed.code': 'Ha ea fihla (khoutu {{code}})',
  'inbox.message.failed.WINDOW_CLOSED': 'Ha ea fihla: ha ba so ngole ho uena lihoreng tse 24 tse fetileng, kahoo sena se ne se hloka mohlala o amohetsoeng.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE': 'Ha e a fihla: WhatsApp e e hanne ka lebaka la bothata ba tefo lehlakoreng la rona, e seng la hao. Re a bo lokisa.',
  'inbox.message.failed.UNDELIVERABLE': 'Ha ea fihla: WhatsApp e hlolehile ho e isa nomorong eo.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED': 'Ha ea fihla: nomoro eo u rometseng ka eona ha e ngolisoa ho WhatsApp.',
  'inbox.message.failed.MEDIA_NOT_FETCHED': 'Ha ea fihla: WhatsApp e hlolehile ho lata faele e khomaretsoeng.',
  'inbox.message.failed.META_REFUSED': 'Ha ea fihla: WhatsApp e e amohetse eaba ha e e fihlise.',
  'inbox.message.failed.notCharged': 'Ha ua lefisoa bakeng sa sena.',
  'inbox.message.template.marker': 'Mohlala · {{name}}',
  'inbox.message.template.noWording': 'Mantswe a molaetsa ona wa mohlala ha a ka a ngolwa ha o romelwa.',
  'inbox.message.template.noWording.named': 'Mantswe a “{{name}}” ha a ka a ngolwa ha ona o tswa.',
  'inbox.message.template.buttons': 'Dikonopo molaetseng ona',
  'inbox.message.template.header.IMAGE': 'Setshwantsho ka hodimo',
  'inbox.message.template.header.VIDEO': 'Video ka hodimo',
  'inbox.message.template.header.DOCUMENT': 'Faele ka hodimo',
  'inbox.message.template.header.LOCATION': 'Sebaka ka hodimo',
  'inbox.message.failed.WINDOW_CLOSED.next': 'Ho e na le hoo romela mohlala o amohetsweng. Ha ba se ba arabile o ka ngola ka bolokolohi hape dihora tse ding tse 24.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.next': 'Ha ho hlokahale hore o etse letho ho Meta. Melaetsa e tla qala ho feta hape hang ha re bo lokisitse.',
  'inbox.message.failed.UNDELIVERABLE.next': 'Hlahloba hore nomoro e nepahetse le hore e sebedisa WhatsApp. Haeba e nepahetse, motho eo a ka be a thibetse melaetsa e tswang kgwebong ya hao.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED.next': 'Bula Dinomoro mme o hokahanye nomoro ena hape. Haeba e kopa khoutu, re tla o tataisa netefatsong.',
  'inbox.message.failed.MEDIA_NOT_FETCHED.next': 'E romele hape ka faele e kentsweng mona ho fapana le e hokahantsweng, kapa o hlahlobe hore sehokelo se bulehile mme se ntse se sebetsa.',
  'inbox.message.failed.META_REFUSED.next': 'Leka hape ka metsotso e seng mekae. Haeba ho tswela pele, re bolelle mme re tla sheba.',
  'inbox.details.title': 'Dintlha tsa molaetsa',
  'inbox.details.open': 'Ho etsahetseng ka molaetsa ona',
  'inbox.details.close': 'Kwala dintlha tsa molaetsa',
  'inbox.details.error': 'Dintlha tsa molaetsa ona di ke ke tsa kgona ho jarwa.',
  'inbox.details.timeline': 'Se etsahetseng',
  'inbox.details.timeline.sent': 'E rometswe',
  'inbox.details.timeline.received': 'E amohetswe',
  'inbox.details.timeline.delivered': 'E fihlile',
  'inbox.details.timeline.read': 'E balilwe',
  'inbox.details.timeline.failed': 'Ha e a fihla',
  'inbox.details.timeline.later': '{{duration}} hamorao',
  'inbox.details.read.absent': 'Ha ho netefatso ya ho bala. WhatsApp e romela e le nngwe feela ha moamohedi a bulile dinetefatso tsa ho bala, kahoo sena se ka dula se se na letho leha ba se ba balile molaetsa wa hao.',
  'inbox.details.delivered.pending': 'WhatsApp ha e so netefatse ho fihla ha yona.',
  'inbox.details.why': 'Hobaneng e sa fihla',
  'inbox.details.why.metaCode': 'Khoutu ya WhatsApp',
  'inbox.details.why.metaWords': 'WhatsApp e itse',
  'inbox.details.cost': 'E bitsitse bokae',
  'inbox.details.cost.unknown': 'Ha rea kgona ho bala buka ya ditjeo bakeng sa molaetsa ona, kahoo ha re kgone ho bolela hore o bitsitse bokae.',
  'inbox.details.cost.rate': 'Tefo',
  'inbox.details.cost.worked': 'E baletswe e le',
  'inbox.details.cost.workedValue': '{{units}} × {{rate}}',
  'inbox.details.cost.chargedAt': 'E lefisitswe',
  'inbox.details.cost.refunded': '{{amount}} e busitswe hobane WhatsApp ha e a fihlisa molaetsa ona.',
  'inbox.details.sent': 'Se rometsweng',
  'inbox.details.template': 'Mohlala',
  'inbox.details.language': 'Puo',
  'inbox.details.category': 'Sehlopha',
  'inbox.details.wording': 'Mantswe',
  'inbox.details.wording.recovered': 'A fumanwe mohlaleng jwalo ka ha o le teng hona jwale, kahoo a ka fapana le se rometsweng.',
  'inbox.details.wording.lost': 'Ha a ka a ngolwa ha sena se romelwa, mme mohlala ha o sa le teng.',
  'inbox.details.params': 'Boleng bo kentsweng',
  'inbox.details.param.header': 'Sehlooho',
  'inbox.details.param.body': 'Molaetsa',
  'inbox.details.param.button': 'Konopo',
  'inbox.details.file.name': 'Lebitso la faele',
  'inbox.details.file.type': 'Mofuta wa faele',
  'inbox.details.file.size': 'Boholo ba faele',
  'inbox.details.where': 'Moo e ileng teng',
  'inbox.details.whereFrom': 'Moo e tswang teng',
  'inbox.details.to': 'Ho',
  'inbox.details.from': 'Ho tswa ho',
  'inbox.details.sentBy': 'E rometswe ke',
  'inbox.details.sentBy.you': 'Wena',
  'inbox.details.sentBy.teammate': 'Setho sa sehlopha',
  'inbox.details.sentBy.bot': 'Chatbot ya hao',
  'inbox.details.sentBy.broadcast': 'Phatlalatso',
  'inbox.details.sentBy.platform': 'RepletoryReach',
  'inbox.details.support': 'Bakeng sa tshehetso',
  'inbox.details.metaId.hint': 'Qolla sena ha o botsa WhatsApp ka molaetsa ona.',
  'inbox.details.copy': 'Kopitsa',
  'inbox.details.copied': 'E kopitsitswe',
  'inbox.channel.whatsapp': 'WhatsApp',
  'inbox.channel.messenger': 'Messenger',
  'inbox.channel.instagram': 'Instagram',
  'inbox.window.closed.body.messenger': 'Messenger e romela karabo feela ka hare ho dihora tse 24 kamora molaetsa wa ho qetela wa motho, mme nako eo e fetile. Karabo ya hao e tla ba fihlela ha ba ngola hape.',
  'inbox.window.closed.body.instagram': 'Instagram e romela karabo feela ka hare ho dihora tse 24 kamora molaetsa wa ho qetela wa motho, mme nako eo e fetile. Karabo ya hao e tla ba fihlela ha ba ngola hape.',
  'inbox.send.notMetered': 'Molaetsa o rometswe, empa ha re a kgona ho ngola hore o bitsa bokae. Tshehetso e tsebisitswe; ha ho letho le lefiswang habedi.',
  'inbox.sidebar.title': 'Litlhophiso tsa moqoqo',
  'inbox.sidebar.toggle': 'Litlhophiso',
  'inbox.sidebar.close': 'Koala litlhophiso tsa moqoqo',
  'inbox.sidebar.details': 'Dintlha',
  'inbox.sidebar.shortcuts': 'Likhutsufatso tsa keyboard',
  'inbox.notes.visibility': 'E bonoa ke sehlopha sa hao feela',
  'inbox.lock.hint': 'Notlela moqoqo ona hore ho se be motho e mong ea arabang ha u ntse u araba',
  'inbox.shortcuts.send': 'Romela karabo',
  'inbox.shortcuts.newline': 'Qala mola o mocha',
  'inbox.shortcuts.canned': 'Bula likarabo tse lokiselitsoeng',
  'inbox.shortcuts.pick': 'Tsamaea har\'a likarabo tse lokiselitsoeng',
  'inbox.shortcuts.insert': 'Kenya karabo e khethiloeng',
  'inbox.shortcuts.dismiss': 'Koala lenane la likarabo tse lokiselitsoeng',
  'inbox.shortcuts.key.enter': 'Enter',
  'inbox.shortcuts.key.shiftEnter': 'Shift+Enter',
  'inbox.shortcuts.key.slash': '/',
  'inbox.shortcuts.key.arrows': '↑ ↓',
  'inbox.shortcuts.key.enterTab': 'Enter kapa Tab',
  'inbox.shortcuts.key.esc': 'Esc',
  'inbox.bot.label': 'Bot',
  'inbox.bot.paused': 'Bot e emisitsoe',
  'inbox.bot.active': 'Bot ea araba',
  'inbox.bot.resumesAt': 'Bot e tla araba hape ho tloha ka {{time}}, ntle le haeba sehlopha se araba pele ho moo.',
  'inbox.bot.untilResolved': 'Bot e lula e khutsitse ho fihlela moqoqo ona o rarollotsoe kapa o e tsoela pele.',
  'inbox.bot.activeHint': 'Bot e araba melaetsa e mecha. Ha o iketsetsa karabo, e e emisa lihora tse 24.',
  'inbox.bot.resume': 'Tsoela pele ka bot',
  'inbox.bot.pause': 'Emisa bot',
  'inbox.bot.resumed': 'Bot e tla araba moqoqo ona hape.',
  'inbox.bot.pausedToast': 'Bot e emisitsoe moqoqong ona.',
  'inbox.bot.failed': 'Bot ha ea ka ea fetoloa moqoqong ona.',
  'inbox.block.label': 'Moreki',
  'inbox.block.blocked': 'O thibetswe',
  'inbox.block.notBlocked': 'Ha a thibelwa',
  'inbox.block.action': 'Thibela moreki',
  'inbox.block.unblock': 'Tlosa thibelo',
  'inbox.block.title': 'Thibela moreki enwa?',
  'inbox.block.body': 'Bot e ke ke ya mo araba mme ha ho motho sehlopheng sa hao ya ka mo ngollang ho fihlela o tlosa thibelo. Melaetsa ya hae e ntse e bolokwa mona.',
  'inbox.block.reason': 'Lebaka',
  'inbox.block.reasonHint': 'Bakeng sa sehlopha sa hao. Moreki ha a le bone.',
  'inbox.block.alsoWhatsapp': 'Thibela le nomoro ena ho WhatsApp',
  'inbox.block.alsoWhatsappHint': 'Ba ke ke ba kgona ho romela molaetsa nomorong ya hao ho hang. WhatsApp e thibela feela motho ya ngotseng dihoreng tse 24 tse fetileng.',
  'inbox.block.confirm': 'Thibela',
  'inbox.block.done': 'Moreki o thibetswe.',
  'inbox.block.doneWhatsapp': 'Moreki o thibetswe mona le ho WhatsApp.',
  'inbox.block.whatsappFailed': 'O thibetswe mona, empa WhatsApp ha e a thibela nomoro: {{error}}',
  'inbox.block.unblocked': 'Thibelo ya moreki e tlositswe.',
  'inbox.block.failed': 'Moreki ha a kgona ho thibelwa kapa ho tloswa thibelo.',
  'inbox.block.bar': 'O thibetse moreki enwa. Bot ha e mo arabe mme dikarabo di tjhesitswe.',
  'inbox.block.since': 'O thibetswe {{time}}',
  'inbox.block.onWhatsapp': 'O thibetswe le ho WhatsApp'
};

export default inbox;
